import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type State = "loading" | "valid" | "already" | "invalid" | "done" | "error";

const Unsubscribe = () => {
  const [state, setState] = useState<State>("loading");
  const [submitting, setSubmitting] = useState(false);
  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    document.title = "Unsubscribe | Oz Move";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content") ?? "";
    desc?.setAttribute(
      "content",
      "Manage your OZ MOVE CO. email preferences. Confirm here to unsubscribe from moving quote updates and notification emails."
    );
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href") ?? "";
    canonical?.setAttribute("href", "https://ozmoveco.com.au/unsubscribe");
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const created = !robots;
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    const previous = robots.content;
    robots.content = "noindex, nofollow";
    return () => {
      if (desc) desc.setAttribute("content", prevDesc);
      if (canonical) canonical.setAttribute("href", prevCanonical);
      if (created) robots?.remove();
      else if (robots) robots.content = previous;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    const validate = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } }
        );
        const data = await res.json();
        if (!res.ok) setState("invalid");
        else if (data.valid) setState("valid");
        else setState("already");
      } catch {
        setState("error");
      }
    };
    validate();
  }, [token]);

  const confirm = async () => {
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
      body: { token },
    });
    setSubmitting(false);
    if (error) setState("error");
    else if (data?.success) setState("done");
    else setState("already");
  };

  const message: Record<State, string> = {
    loading: "Checking your link…",
    valid: "Click below to stop receiving emails from Oz Move at this address.",
    already: "This address is already unsubscribed. No further emails will be sent.",
    invalid: "This unsubscribe link is invalid or has expired.",
    done: "You've been unsubscribed. We won't email this address again.",
    error: "Something went wrong. Please try again, or contact us at contact@ozmoveco.com.au.",
  };

  return (
    <main className="min-h-screen bg-[#f7f7f7] flex items-center justify-center px-5 py-20">
        <div className="w-full max-w-md bg-white/80 backdrop-blur border-2 border-[#1a1a1a]/10 rounded-xl p-8 text-center shadow-sm">
          <p className="text-xs font-extrabold tracking-[2px] text-[#1a1a1a]/70 mb-4">OZ MOVE</p>
          <h1 className="text-2xl font-black text-[#1a1a1a] mb-3">Email preferences</h1>
          <p className="text-[#1a1a1a]/75 text-base mb-7">{message[state]}</p>
          {state === "valid" && (
            <button
              onClick={confirm}
              disabled={submitting}
              className="w-full bg-[#1a1a1a] text-[#FFC300] py-3.5 text-base font-bold rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {submitting ? "Unsubscribing…" : "Confirm unsubscribe"}
            </button>
          )}
          <a href="/" className="block mt-6 text-sm font-semibold text-[#1a1a1a] underline">
            Back to Oz Move
          </a>
        </div>
    </main>
  );
};

export default Unsubscribe;